import {
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  SelectChangeEvent,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import useAgencyData from '../../../networkRequest/useAgencyData';
import { useUser } from '../../../hooks/useUser';
import { updateOfficial, OfficialData } from '../../../networkRequest/officialsService';
import { z as zod } from 'zod';
import AddAgencyOfficialDialogContent from '../../UserManagementPage/components/AddAgencyOfficialDialog/AddAgencyOfficialDialogContent';
import { FormOfficialData, OfficialFormHelpers } from '../../../utils/officialFormHelpers';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import DOMPurify from 'dompurify';

interface EditUserDialogProps {
  open: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currentOfficial: OfficialData;
  onError: React.Dispatch<React.SetStateAction<string>>;
  onSuccess: (updatedOfficial: OfficialData) => void;
  refreshData: () => Promise<void>;
}

// we'd have to deeply copy this anyways, so just putting into friendly format manually for ease of readability.
// just add in the other items we want in the future, if any.
const createFormUserData = (currentOfficial: OfficialData): FormOfficialData => {
  return {
    id: currentOfficial.id,
    first_name: currentOfficial.first_name,
    last_name: currentOfficial.last_name,
    title: currentOfficial.title || '',
    email: currentOfficial.email || '',
    badge_number: currentOfficial.badge_number || '',
    role: currentOfficial.role.name,
    rank: currentOfficial.rank || '',
    phone_number: currentOfficial.phone_number || '',
    armed_permission: currentOfficial.armed_permission,
    employment_type: currentOfficial.employment_type
  };
};
const EditUserDialog = ({
  open,
  setDialogOpen,
  currentOfficial,
  onError,
  onSuccess,
  refreshData
}: EditUserDialogProps) => {
  const { user } = useUser();
  const theme = useTheme();
  const padding = theme.spacing(3);
  const [formOfficialData, setFormOfficialData] = useState(createFormUserData(currentOfficial));
  const [errors, setErrors] = useState<zod.ZodError<any> | null>(null);
  const [loading, setLoading] = useState(false);
  const { agencyOfficialRoles, error: agencyError, setError: setAgencyError } = useAgencyData(user);
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);

  // Clear validation errors when the dialog is closed
  useEffect(
    () => {
      if (!open) {
        setErrors(null);
      }
    },
    [open]
  );

  // When the form data changes, enable the submit button only if the
  // form data is different to the initial data
  useEffect(
    () => {
      if (!user) {
        return;
      }
      const changedData = OfficialFormHelpers.formatChangedOfficialFormData(
        formOfficialData,
        createFormUserData(currentOfficial),
        agencyOfficialRoles
      );
      setSubmitEnabled(changedData !== null);
    },
    [agencyOfficialRoles, currentOfficial, formOfficialData, user]
  );

  useEffect(() => {
    if (agencyError) onError(agencyError);
  }, [agencyError, onError]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!user) {
      throw new Error('User not found');
    }

    const validForm = OfficialFormHelpers.validateForm(formOfficialData, agencyOfficialRoles, true, setErrors, onError);

    if (!validForm) {
      setLoading(false);
      return;
    }

    try {
      const formData = OfficialFormHelpers.formatOfficialFormData(formOfficialData, agencyOfficialRoles);
      if (!formData) return;
      const { data, error } = await updateOfficial(user.accessToken, formData);
      if (data) {
        clearDialog();

        const emailChanged = formOfficialData.email !== currentOfficial.email;
        if (emailChanged) {
          // Delayed refresh to allow email verification background job to complete
          setTimeout(() => refreshData(), 750);
        } else {
          refreshData();
        }
        setFormOfficialData(createFormUserData(data));
        onSuccess(data);
      } else if (error) {
        onError(error);
      }
    } catch (error: any) {
      onError(error.message);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // must have separate handlers or a type guard to appease TS
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormOfficialData({ ...formOfficialData, [name]: value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    const sanitizedValue = DOMPurify.sanitize(value);
    const newFormOfficialData = { ...formOfficialData, [name]: sanitizedValue };
    // When the role is changed, if there are validation errors, re-run
    // validation. This is done to update the email field validation error
    // when it changes between required and optional
    if (name === 'role' && errors !== null) {
      OfficialFormHelpers.validateForm(newFormOfficialData, agencyOfficialRoles, true, setErrors, onError);
    }
    setFormOfficialData(newFormOfficialData);
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    setFormOfficialData({ ...formOfficialData, [name]: checked });
  };

  const closeDialog = () => setDialogOpen(false);

  const clearDialog = () => {
    setAgencyError('');
    closeDialog();
    setFormOfficialData(createFormUserData(currentOfficial));
  };
  return (
    <Dialog
      open={open}
      onClose={() => setDialogOpen(false)}
      maxWidth="md"
      fullWidth
      slotProps={{ backdrop: { invisible: true }, paper: { style: { borderRadius: 8 } } }}
    >
      <IconButton
        aria-label="close"
        onClick={closeDialog}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500]
        })}
      >
        <CloseIcon sx={{ fontSize: 32, color: theme.palette.grey[600] }} />
      </IconButton>
      <DialogTitle sx={{ padding }}>Edit User</DialogTitle>
      <form onSubmit={handleSubmit} role="form">
        <AddAgencyOfficialDialogContent
          handleChange={{ handleInputChange, handleSelectChange, handleSwitchChange }}
          formOfficialData={formOfficialData}
          roles={agencyOfficialRoles}
          formErrors={errors}
        />
        <DialogActions sx={{ padding }}>
          <SecondaryButton action={clearDialog}>Cancel</SecondaryButton>
          <PrimaryButton
            type="submit"
            loading={loading}
            disabled={!submitEnabled}
            minHeight="2.25rem"
            minWidth="7rem"
          >
            Submit
          </PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditUserDialog;
